// Friends + block list panel. Shows accepted friends (with online/offline +
// zone badge), incoming pending requests (accept/decline), outgoing pending,
// and the block list. Actions round-trip through the normal WS protocol.

import type { ClientStore } from "../state.js";
import type { Net } from "../net.js";
import { openPanel } from "./shared_panel.js";

export function openFriendsPanel(store: ClientStore, net: Net): void {
  net.send({ t: "friend_list" });
  net.send({ t: "block_list" });
  openPanel({
    id: "panel-friends",
    title: "Friends & Blocks",
    store,
    render(state) {
      const accepted = state.friends.filter((f) => f.status === "accepted");
      const incoming = state.friends.filter((f) => f.status === "pending" && f.direction === "incoming");
      const outgoing = state.friends.filter((f) => f.status === "pending" && f.direction === "outgoing");
      const rows = (arr: typeof state.friends, kind: "accepted" | "incoming" | "outgoing") => arr.length
        ? arr.map((f) => `<tr>
            <td>${f.name}</td>
            <td>${f.online ? `<span class="badge-on">online</span> <span class="muted">${f.zone}</span>` : `<span class="muted">offline</span>`}</td>
            <td>${actionButtons(f.char_id, kind)}</td>
          </tr>`).join("")
        : `<tr><td colspan="3" class="muted">No ${kind} friends.</td></tr>`;
      const blockRows = state.blocks.length
        ? state.blocks.map((b) => `<tr><td>${b.name}</td><td class="muted">${b.reason || "—"}</td><td><button data-unblock="${b.char_id}">Unblock</button></td></tr>`).join("")
        : `<tr><td colspan="3" class="muted">No blocks.</td></tr>`;
      return `
        <style>
          .friends-panel table { width:100%; border-collapse: collapse; font-size:12px; margin:4px 0 12px; }
          .friends-panel th, .friends-panel td { text-align:left; padding:4px 6px; border-bottom:1px solid var(--panel-line,#2a2f3d); }
          .friends-panel .badge-on { color:#5fb878; font-size:10px; text-transform:uppercase; letter-spacing:0.08em; }
          .friends-panel section { margin-bottom:14px; }
          .friends-panel button { font:inherit; font-size:11px; padding:2px 6px; }
          .friends-panel .request-bar { display:flex; gap:6px; margin-bottom:10px; }
          .friends-panel .request-bar input { flex:1; }
        </style>
        <div class="friends-panel">
          <div class="request-bar">
            <input id="fp-add" placeholder="Character name" />
            <button id="fp-send">Send friend request</button>
            <button id="fp-block">Block…</button>
          </div>
          ${incoming.length ? `<section><h3>Incoming requests (${incoming.length})</h3><table><thead><tr><th>Name</th><th>Status</th><th>Actions</th></tr></thead><tbody>${rows(incoming, "incoming")}</tbody></table></section>` : ""}
          <section><h3>Friends (${accepted.length})</h3><table><thead><tr><th>Name</th><th>Status</th><th>Actions</th></tr></thead><tbody>${rows(accepted, "accepted")}</tbody></table></section>
          ${outgoing.length ? `<section><h3>Outgoing requests (${outgoing.length})</h3><table><thead><tr><th>Name</th><th>Status</th><th>Actions</th></tr></thead><tbody>${rows(outgoing, "outgoing")}</tbody></table></section>` : ""}
          <section><h3>Blocked (${state.blocks.length})</h3><table><thead><tr><th>Name</th><th>Reason</th><th></th></tr></thead><tbody>${blockRows}</tbody></table></section>
        </div>
      `;
    },
    bind(root) {
      const input = root.querySelector<HTMLInputElement>("#fp-add");
      root.querySelector("#fp-send")?.addEventListener("click", () => {
        const v = (input?.value ?? "").trim();
        if (!v) return;
        net.send({ t: "friend_request", target: v });
        if (input) input.value = "";
      });
      root.querySelector("#fp-block")?.addEventListener("click", () => {
        const v = (input?.value ?? "").trim();
        if (!v) return;
        const reason = prompt("Reason for block (optional):") ?? "";
        net.send({ t: "block_add", target: v, reason });
        if (input) input.value = "";
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-accept]").forEach((b) => {
        b.addEventListener("click", () => net.send({ t: "friend_accept", friend_char_id: Number(b.dataset.accept) }));
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-decline]").forEach((b) => {
        b.addEventListener("click", () => net.send({ t: "friend_decline", friend_char_id: Number(b.dataset.decline) }));
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-remove]").forEach((b) => {
        b.addEventListener("click", () => {
          if (!confirm("Remove friend?")) return;
          net.send({ t: "friend_remove", friend_char_id: Number(b.dataset.remove) });
        });
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-cancel]").forEach((b) => {
        b.addEventListener("click", () => net.send({ t: "friend_remove", friend_char_id: Number(b.dataset.cancel) }));
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-unblock]").forEach((b) => {
        b.addEventListener("click", () => net.send({ t: "block_remove", char_id: Number(b.dataset.unblock) }));
      });
    },
  });
}

function actionButtons(charId: number, kind: "accepted" | "incoming" | "outgoing"): string {
  if (kind === "incoming") {
    return `<button data-accept="${charId}">Accept</button> <button data-decline="${charId}">Decline</button>`;
  }
  if (kind === "outgoing") {
    return `<button data-cancel="${charId}">Cancel</button>`;
  }
  return `<button data-remove="${charId}">Remove</button>`;
}
